import type { PreloadDistance } from '~/composables/useCardPreloadBudget';
import type { ShakaPlayer } from '~/composables/useSharedVideoPlayer';
import type { PrefetchVariant } from '~/core/entities/property';
import type { Ref } from 'vue';

import {
	useElementHover,
	useEventListener,
	useIntersectionObserver,
	useTimeoutFn
} from '@vueuse/core';
import {
	releaseCardPreload,
	requestCardPreload
} from '~/composables/useCardPreloadBudget';
import {
	getSavedBandwidthEstimate,
	registerActivePlayer,
	unregisterActivePlayer
} from '~/composables/useHlsPlayer/shakaLoader';
import {
	elementShortSide,
	rememberSurfaceSide
} from '~/composables/useHlsPlayer/startTarget';
import {
	createPlayer,
	destroyPlayer
} from '~/composables/useSharedVideoPlayer';
import { useVideoQoE } from '~/composables/useVideoQoE';
import { getProxiedVideoUrl } from '~/composables/useVideoUrls';
import {
	createChainWarmer,
	wasVideoWarmed
} from '~/composables/useVideoWarmup';
import { ErrorCategory } from '~/core/ports/datadog';
import { useDatadog } from '~~/composables/useDatadog';
import { computed } from 'vue';

import { isIOSNativePlayback } from '~/components/PropertyReelsFeed/useIOSSharedVideo';

// On slow networks, cards in the preload margin zone wait longer before
// initialising their player so the active card keeps the bandwidth.
function getMarginPreloadDelay(): number {
	const bps = getSavedBandwidthEstimate();
	if (bps === null || bps >= 4_000_000) return 600; // fast / unknown: standard
	if (bps >= 2_000_000) return 1_500; // medium: 1.5s
	return 3_000; // slow: 3s
}

/**
 * Awaits the first `canplay` or completes immediately if `signal` is aborted (ex.: unmount).
 * Always resolves; the caller must check `signal.aborted` and `shouldPlay` before `play()`.
 */
function waitUntilVideoCanPlay(
	video: HTMLVideoElement,
	signal: AbortSignal
): Promise<void> {
	if (video.readyState !== 0) return Promise.resolve();
	if (signal.aborted) return Promise.resolve();
	return new Promise(resolve => {
		const finish = () => resolve();
		video.addEventListener('canplay', finish, { once: true, signal });
		signal.addEventListener('abort', finish, { once: true });
	});
}

interface UseVideoCardPlayerOptions {
	videoUrl: Ref<string | null>;
	isActive: Ref<boolean>;
	hoverToPlay: Ref<boolean>;
	preloadWhenVisible?: Ref<boolean>;
	index: Ref<number>;
	playerRootMargin?: string;
	/** Fonte de hover externa. Quando omitido, usa `useElementHover(cardRef)`. */
	isHovered?: Ref<boolean>;
	/** Escada HLS ja resolvida pelo backend: permite aquecer a cadeia em paralelo. */
	prefetchVariants?: Ref<PrefetchVariant[] | null | undefined>;
	/**
	 * Distancia do foco (useCarouselPreloadWindow). `null` = fora da janela,
	 * nao abre cadeia; `undefined` = sem paginacao, decisao so por viewport.
	 */
	preloadDistance?: Ref<PreloadDistance | null | undefined>;
}

let cardPlayerSeq = 0;

export function useVideoCardPlayer({
	videoUrl,
	isActive,
	hoverToPlay,
	preloadWhenVisible = computed(() => false),
	index,
	playerRootMargin = '100px',
	isHovered: externalHovered,
	prefetchVariants,
	preloadDistance
}: UseVideoCardPlayerOptions) {
	const tag = () => `[Video #${index.value}]`;
	const { trackError } = useDatadog();

	// Identidade no teto de vagas: URL repete entre superficies e o indice
	// muda quando a lista pagina.
	const preloadBudgetId = `card-${++cardPlayerSeq}`;

	function hasVideoSource(): boolean {
		return Boolean(videoUrl.value?.trim());
	}

	const cardRef = ref<HTMLDivElement | null>(null);
	const videoRef = ref<HTMLVideoElement | null>(null);
	const shakaPlayer = shallowRef<ShakaPlayer | null>(null);

	const isHovered = externalHovered ?? useElementHover(cardRef);
	const isVideoPlaying = ref(false);
	const isVisible = ref(false);
	const isPlayerReady = ref(false);
	const isInitializing = ref(false);
	const isAutoplayBlocked = ref(false);
	const videoProgress = ref(0);
	const isMuted = ref(true);
	let initGeneration = 0;
	let savedCurrentTime = 0;
	let initErrorCount = 0;
	const MAX_INIT_ERRORS = 3;
	/** In-flight `canplay` wait; aborted on unmount or superseded by a newer `safePlay`. */
	let pendingCanPlayAbort: AbortController | null = null;

	const shouldPlay = computed(
		() => (hoverToPlay.value && isHovered.value) || isActive.value
	);
	const shouldPreload = computed(
		() => hoverToPlay.value || preloadWhenVisible.value
	);
	const isLoading = computed(
		() => shouldPlay.value && !isVideoPlaying.value && !isAutoplayBlocked.value
	);
	const proxiedVideoUrl = computed(() => getProxiedVideoUrl(videoUrl.value));

	// QoE telemetry — carousel/card previews, separated from the feed via surface.
	const {
		beginAttempt: beginQoEAttempt,
		finalize: finalizeQoE,
		abort: abortQoE
	} = useVideoQoE({
		videoElement: videoRef,
		surface: 'shorts_carousel',
		pipeline: isIOSNativePlayback() ? 'ios_native' : 'shaka',
		getVideoUrl: () => proxiedVideoUrl.value,
		getShakaPlayer: () => shakaPlayer.value,
		wasWarmed: wasVideoWarmed
	});

	function handleMuteToggle() {
		isMuted.value = !isMuted.value;
		if (videoRef.value) {
			videoRef.value.muted = isMuted.value;
		}
	}

	function seekTo(ratio: number) {
		if (videoRef.value && videoRef.value.duration) {
			videoRef.value.currentTime = ratio * videoRef.value.duration;
		}
	}

	async function safePlay(video: HTMLVideoElement) {
		if (video.readyState === 0) {
			pendingCanPlayAbort?.abort();
			const canPlayWait = new AbortController();
			pendingCanPlayAbort = canPlayWait;
			try {
				await waitUntilVideoCanPlay(video, canPlayWait.signal);
			} finally {
				if (pendingCanPlayAbort === canPlayWait) {
					pendingCanPlayAbort = null;
				}
			}
			if (canPlayWait.signal.aborted || !shouldPlay.value) return;
		}

		isAutoplayBlocked.value = false;

		try {
			await video.play();
		} catch (err) {
			if (err instanceof DOMException && err.name === 'NotAllowedError') {
				video.muted = true;
				isMuted.value = true;
				try {
					await video.play();
				} catch (retryErr) {
					if (
						retryErr instanceof DOMException &&
						retryErr.name === 'NotAllowedError'
					) {
						isAutoplayBlocked.value = true;
						// QoE clock would count the wait for the user's tap — discard.
						abortQoE();
					}
					console.error(tag(), 'safePlay: muted retry also failed', retryErr);
				}
			} else {
				console.error(tag(), 'safePlay: play() failed', err);
			}
		}
	}

	useEventListener(videoRef, 'playing', () => {
		rememberSurfaceSide('card', videoRef.value);
		isVideoPlaying.value = true;
		isAutoplayBlocked.value = false;
	});

	useEventListener(videoRef, 'pause', () => {
		isVideoPlaying.value = false;
	});

	useEventListener(videoRef, 'error', () => {
		const err = videoRef.value?.error;
		finalizeQoE('error');
		console.error(tag(), 'EVENT error', {
			code: err?.code,
			message: err?.message
		});
	});

	useEventListener(videoRef, 'timeupdate', () => {
		if (videoRef.value && videoRef.value.duration) {
			videoProgress.value =
				(videoRef.value.currentTime / videoRef.value.duration) * 100;
		}
	});

	// Cards visible on screen init fast; cards only in the preload margin wait
	// longer — and scale the delay to the saved network speed so slow-network
	// users don't waste bandwidth on off-screen cards while the active one buffers.
	const { start: startPreloadVisible, stop: stopPreloadVisible } = useTimeoutFn(
		() => {
			if (!isPlayerReady.value && hasVideoSource()) initPlayer();
		},
		100,
		{ immediate: false }
	);

	let preloadMarginTimer: ReturnType<typeof setTimeout> | null = null;

	function startPreloadMargin() {
		if (preloadMarginTimer) clearTimeout(preloadMarginTimer);
		preloadMarginTimer = setTimeout(() => {
			preloadMarginTimer = null;
			if (!isPlayerReady.value && hasVideoSource()) initPlayer();
		}, getMarginPreloadDelay());
	}

	function stopPreloadMargin() {
		if (preloadMarginTimer) {
			clearTimeout(preloadMarginTimer);
			preloadMarginTimer = null;
		}
	}

	// Warmer proprio por card: o abort do prefetch de intencao e global e
	// reusa-lo faria os cards da mesma tela se abortarem em cadeia.
	const chainWarmer = createChainWarmer(() => elementShortSide(videoRef.value));
	const abortChainWarm = chainWarmer.abort;

	function warmChainFromPayload() {
		chainWarmer.warm(proxiedVideoUrl.value, prefetchVariants?.value);
	}

	function beginPreloadTimers(ratio: number) {
		warmChainFromPayload();
		if (ratio > 0) {
			stopPreloadMargin();
			startPreloadVisible();
		} else {
			startPreloadMargin();
		}
	}

	function startPreload(ratio: number) {
		// O card que vai TOCAR nunca passa pelo teto: atrasaria o video em foco.
		if (shouldPlay.value) {
			releaseCardPreload(preloadBudgetId);
			beginPreloadTimers(ratio);
			return;
		}

		const windowDistance = preloadDistance?.value;
		// Fora da janela direcional: nao gasta cano e devolve o que ja tinha.
		if (windowDistance === null) {
			stopPreload();
			cleanupPlayer();
			return;
		}

		requestCardPreload(
			preloadBudgetId,
			windowDistance ?? (ratio > 0 ? 0 : 1),
			() => beginPreloadTimers(ratio),
			() => {
				// Cedeu a vaga para um card mais perto do foco.
				stopPreloadVisible();
				stopPreloadMargin();
				abortChainWarm();
				cleanupPlayer();
			}
		);
	}

	function stopPreload() {
		releaseCardPreload(preloadBudgetId);
		stopPreloadVisible();
		stopPreloadMargin();
		abortChainWarm();
	}

	// Ultimo ratio observado: a janela direcional muda sem evento de intersecao.
	let lastIntersectionRatio: number | null = null;

	useIntersectionObserver(
		cardRef,
		entries => {
			const entry = entries[0];
			if (!entry) return;

			lastIntersectionRatio = entry.isIntersecting
				? entry.intersectionRatio
				: null;

			if (entry.isIntersecting) {
				isVisible.value = entry.intersectionRatio > 0;
				if (!hasVideoSource()) return;
				// Re-entering the viewport with shouldPlay still true replays the
				// video without the watcher refiring — reopen the QoE attempt
				// (idempotent while one is already running for this URL).
				if (shouldPlay.value) beginQoEAttempt();
				if (shouldPreload.value) {
					startPreload(entry.intersectionRatio);
				} else if (shouldPlay.value && !isPlayerReady.value) {
					cancelPlayDebounce();
					startPlayDebounce();
				}
			} else {
				isVisible.value = false;
				cancelPlayDebounce();
				stopPreload();
				// Scrolled out of view while playing — the play session is over.
				finalizeQoE('navigated');
				cleanupPlayer();
			}
		},
		{ rootMargin: playerRootMargin, threshold: [0, 0.01] }
	);

	// A permissao de pre-carga muda sem o viewport mudar: sem este watcher o
	// card ja intersectando esperaria um evento de intersecao que nunca vem.
	watch([shouldPreload, () => preloadDistance?.value], () => {
		if (lastIntersectionRatio === null || !hasVideoSource()) return;
		if (!shouldPreload.value) return;
		startPreload(lastIntersectionRatio);
	});

	async function initPlayer() {
		if (
			!hasVideoSource() ||
			!proxiedVideoUrl.value ||
			!videoRef.value ||
			isPlayerReady.value ||
			isInitializing.value ||
			initErrorCount >= MAX_INIT_ERRORS
		)
			return;

		const initStart = performance.now();

		isInitializing.value = true;
		const generation = ++initGeneration;

		try {
			const startTime = savedCurrentTime > 0 ? savedCurrentTime : undefined;
			const url = proxiedVideoUrl.value;

			// Preload usa bufferingGoal 3 (nao 10): card que talvez nem toque.
			const player = await createPlayer(url, videoRef.value, {
				startTime,
				isPreload: !shouldPlay.value
			});

			if (generation !== initGeneration || !videoRef.value) {
				destroyPlayer(player);
				return;
			}

			shakaPlayer.value = player;
			isPlayerReady.value = true;

			if (shouldPlay.value && videoRef.value) safePlay(videoRef.value);
		} catch (err) {
			// AbortError = intentional cancel (scroll, timeout) — allow retry, no log.
			const isAbort = err instanceof DOMException && err.name === 'AbortError';
			if (isAbort) return;

			initErrorCount++;
			const elapsed = Math.round(performance.now() - initStart);
			console.error(
				tag(),
				`initPlayer ERROR after ${elapsed}ms (attempt ${initErrorCount}/${MAX_INIT_ERRORS})`,
				err
			);
			trackError({
				message: `[useVideoCardPlayer] initPlayer failed after ${elapsed}ms (attempt ${initErrorCount}/${MAX_INIT_ERRORS}): ${(err as Error)?.message}`,
				source: 'source',
				category: ErrorCategory.UI,
				stack: (err as Error)?.stack
			});
		} finally {
			isInitializing.value = false;
		}
	}

	function cleanupPlayer() {
		initGeneration++;
		initErrorCount = 0;
		if (shakaPlayer.value) {
			if (videoRef.value) savedCurrentTime = videoRef.value.currentTime;
			const video = videoRef.value;
			destroyPlayer(shakaPlayer.value, video ?? undefined);
			shakaPlayer.value = null;
			isPlayerReady.value = false;
			isVideoPlaying.value = false;
			isAutoplayBlocked.value = false;
			releaseVideoElement(video);
		}
	}

	// Solta o elemento de video, nao so o player: o destroy do hls.js desanexa
	// a media mas nao impede o card fora de tela de seguir tocando/bufferizando.
	function releaseVideoElement(video: HTMLVideoElement | null) {
		if (!video) return;
		try {
			video.pause();
			video.muted = true;
			video.removeAttribute('src');
			// Sem o load() o Chrome mantem o buffer da fonte anterior alocado.
			video.load();
		} catch {
			// Elemento ja removido do DOM pelo Vue: nada a soltar.
		}
	}

	const PLAY_DEBOUNCE_MS = 200;

	const { start: startPlayDebounce, stop: cancelPlayDebounce } = useTimeoutFn(
		() => {
			if (shouldPlay.value && !isPlayerReady.value && videoRef.value) {
				stopPreload();
				initPlayer();
			}
		},
		PLAY_DEBOUNCE_MS,
		{ immediate: false }
	);

	watch(
		shouldPlay,
		playing => {
			cancelPlayDebounce();

			const url = proxiedVideoUrl.value;
			if (url) {
				if (playing) registerActivePlayer(url);
				else unregisterActivePlayer(url);
			}

			// QoE attempt tracks the should-play window (activation -> deactivation).
			if (playing && hasVideoSource()) beginQoEAttempt();
			else if (!playing) finalizeQoE('navigated');

			if (!hasVideoSource() || !videoRef.value) return;

			if (playing) {
				if (isPlayerReady.value) {
					safePlay(videoRef.value);
				} else {
					startPlayDebounce();
				}
			} else if (isPlayerReady.value) {
				videoRef.value.pause();
			}
		},
		{ immediate: true }
	);

	onBeforeUnmount(() => {
		pendingCanPlayAbort?.abort();
		pendingCanPlayAbort = null;
		cancelPlayDebounce();
		stopPreload(); // already calls stopPreloadMargin internally
		if (proxiedVideoUrl.value) unregisterActivePlayer(proxiedVideoUrl.value);
		cleanupPlayer();
	});

	return {
		cardRef,
		videoRef,
		isVideoPlaying,
		isVisible,
		isLoading,
		isInitializing,
		isAutoplayBlocked,
		shouldPlay,
		videoProgress,
		isMuted,
		handleMuteToggle,
		seekTo
	};
}
